import { useState, useEffect } from "react";
import { showErrorDialog } from "@/helpers/showErrorDialog";
import Swal from "sweetalert2";
import { APIPaginatedResponse } from "@/interfaces/APIPaginatedResponse";

interface ListService<T> {
  list: (params: { page: number }) => Promise<APIPaginatedResponse<T[]>>;
  remove: (id: string) => Promise<void>;
}

interface Options {
  filters?: any;
  showLoading?: boolean;
}

export default function useFetchAndPaginate<T>(
  service: ListService<T>,
  options?: Options
) {
  const [filters, setFilters] = useState<any>({});
  const [page, setPage] = useState<number>(1);
  const [removeDialogVisible, setRemoveDialogVisible] = useState(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [items, setItems] = useState<T[]>([]);
  const [activeId, setActiveId] = useState<string | undefined>(undefined);

  const fetchData = async () => {
    const filtersParams = options?.filters ?? {};
    const isActive = filters.activeStatus
      ? filters.activeStatus === "active"
        ? "true"
        : "false"
      : undefined;
    const res = await service.list({
      page,
      ...filters,
      isActive,
      ...filtersParams
    });
    setItems(res.data);
    setTotalPages(res.meta.lastPage);
  };

  const handleRemove = async (id: string) => {
    const result = await Swal.fire({
      title: "ต้องการลบใช่หรือไม่?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก"
    });
    if (result.isConfirmed) {
      Swal.showLoading();
      try {
        await service.remove(id);
        await fetchData();
        Swal.close();
        Swal.fire("สำเร็จ", "ทำการลบสำเร็จ", "success");
      } catch (error) {
        Swal.close();
        showErrorDialog(error);
      }
    }
  };

  const handleCLoseConfirmRemoveDialog = () => {
    setRemoveDialogVisible(false);
  };

  const onConfirmRemove = async () => {
    setRemoveDialogVisible(false);
    Swal.showLoading();
    service
      .remove(activeId)
      .then(() => {
        Swal.fire("ลบข้อมูลสำเร็จ", "", "success");
      })
      .then(() => fetchData())
      .catch((error) => {
        showErrorDialog(error);
      })
      .finally(() => {
        Swal.close();
      });
  };

  useEffect(() => {
    const onInit = async () => {
      Swal.showLoading();
      try {
        await fetchData();
        Swal.close();
      } catch (error) {
        console.error(error);
        Swal.close();
      }
    };
    onInit();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page]);

  return {
    items,
    totalPages,
    page,
    setPage,
    handleRemove,
    removeDialogVisible,
    onConfirmRemove,
    handleCLoseConfirmRemoveDialog,
    setActiveId,
    filters,
    setFilters
  };
}
